import React, { useMemo, useState, useEffect, useRef } from 'react';
import useGameBase from '../../hooks/useGameBase';

function QuizGame({ gameState, gamePlayers, user, socket, roomId }) {
  const { submitAction, normalizedState } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const state = useMemo(() => normalizedState || gameState || {}, [normalizedState, gameState]);
  const meId = user ? (user.socketId || user.userId) : null;

  // local selected answer tagged with the round it belongs to
  const [myAnswer, setMyAnswer] = useState({ value: null, round: -1 });

  const disabled = state.finished;
  const timeLimit = state.timeLimit || Math.ceil((state.timePerQuestionMs || 12000) / 1000) || 12;
  const [timeLeft, setTimeLeft] = useState({ value: timeLimit, round: -1 });
  const rafRef = useRef(null);
  const noTransitionRef = useRef(false);
  const [, forceRender] = useState(0);

  // Derive safe display values — fall back to neutral defaults when the stored
  // round doesn't match the current round (avoids one-frame flash of old state)
  const currentRound = state.roundIndex ?? -1;
  const myAnswerValue = myAnswer.round === currentRound ? myAnswer.value : null;
  const timeLeftValue = timeLeft.round === currentRound ? timeLeft.value : timeLimit;
  // format ms into human friendly seconds string
  const formatAnswerTime = (ms) => {
    if (ms === undefined || ms === null) return null;
    const s = ms / 1000;
    if (s >= 60) {
      const mins = Math.floor(s / 60);
      const rem = (s % 60).toFixed(2);
      return `${mins}m ${rem}s`;
    }
    return `${s.toFixed(2)}s`;
  };
  

  const handleAnswer = (opt) => {
    if (disabled || state.phase !== 'question') return;
    try { setMyAnswer({ value: opt, round: state.roundIndex }); } catch (e) {}
    submitAction({ type: 'answer', answer: opt }).catch(()=>{});
  };

  // Sync local selected answer with authoritative server state and reset on new round
  useEffect(() => {
    // Prefer server-provided answer if present
    let serverAns = null;
    if (Array.isArray(state.answers)) {
      const entry = state.answers.find(a => (a && (a.playerId === meId || a.playerId === (meId || ''))));
      serverAns = entry ? entry.answer : null;
    } else if (state.answers && typeof state.answers === 'object' && meId) {
      // explicit check for property (could be null/empty string)
      if (Object.prototype.hasOwnProperty.call(state.answers, meId)) {
        serverAns = state.answers[meId];
      }
    }

    if (serverAns !== null && serverAns !== undefined) {
      setMyAnswer({ value: serverAns, round: state.roundIndex });
      return;
    }

    // if round changed (or no server answer), reset optimistic answer
    setMyAnswer({ value: null, round: state.roundIndex });
  }, [state.roundIndex, state.answers, meId]);

  // reset timeLeft when roundIndex (question number) changes
  useEffect(() => {
    noTransitionRef.current = true;
    setTimeLeft({ value: timeLimit, round: state.roundIndex });
    requestAnimationFrame(() => {
      noTransitionRef.current = false;
      forceRender(n => n + 1);
    });
  }, [state.roundIndex, timeLimit]);

  // Countdown using requestAnimationFrame like MathsQuiz
  useEffect(() => {
    // start countdown when a question begins; use roundIndex so submitting answers (which updates state) doesn't restart it
    if (state.phase === 'question' && !state.finished) {
      let start = Date.now();
      const tick = () => {
        const elapsed = (Date.now() - start) / 1000;
        const pct = Math.min(elapsed / timeLimit, 1);
        setTimeLeft({ value: Math.max(0, Math.ceil(timeLimit - elapsed)), round: state.roundIndex });
        if (pct < 1 && state.phase === 'question' && !state.finished) {
          rafRef.current = requestAnimationFrame(tick);
        }
      };
      rafRef.current = requestAnimationFrame(tick);
      return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    }
    return undefined;
  }, [state.roundIndex, state.phase, state.finished, timeLimit]);

  return (
    <div className="p-4 max-w-lg mx-auto">
        {(state.phase === 'question' || state.phase === 'reveal' ) && (
      <div className="mb-4">
        <div className="text-sm text-gray-600 dark:text-gray-300">Round {Math.min((state.roundIndex||0)+1, state.rounds||0)} of {state.rounds}</div>
      </div>
    )}
      
        
        {state.phase === 'question' && (
          <>
          <div className="bg-white dark:bg-gray-900 rounded-md p-4 mb-4 shadow-sm ">
          <div className="text-md font-medium mb-3 text-gray-700 dark:text-gray-300">{state.current ? state.current.question : 'Waiting...'}</div>
            <div className="grid grid-cols-1 gap-2">
                {((state.current && state.current.options) || []).map((opt, i) => {
                const answered = !!myAnswerValue;
                const isSelected = myAnswerValue !== null && String(myAnswerValue) === String(opt);
                const correctAnswer = state.correctAnswer || (state.current && state.current.correct);
                const isCorrect = correctAnswer && String(opt) === String(correctAnswer);
                let btnClass = 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-gray-100';
                if (isSelected) {
                  btnClass = isCorrect
                    ? 'bg-green-600 text-white dark:bg-green-500'
                    : 'bg-red-600 text-white dark:bg-red-500';
                } else if (answered && isCorrect) {
                  // highlight the correct answer even if this player picked wrong
                  btnClass = 'bg-green-600 text-white dark:bg-green-500';
                }
                return (
                <button
                  key={i}
                  onClick={() => handleAnswer(opt)}
                  disabled={disabled || state.phase !== 'question' || answered}
                  className={`w-full text-left px-3 py-2 rounded transition-colors ${!answered ? 'hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-gray-100' : ''} ${btnClass} ${answered && !isSelected && !isCorrect ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {opt}
                </button>
              )})}
            </div>

            {/* Progress bar + time left like MathsQuiz */}
            <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded mt-4 overflow-hidden">
              <div
                className="h-full bg-yellow-400"
                style={{ width: `${(1 - (timeLeftValue / timeLimit)) * 100}%`, transition: noTransitionRef.current ? 'none' : 'width 0.1s linear' }}
              ></div>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 text-right">{timeLeftValue}s left</div>
            </div>
          </>
        )}

        {state.phase === 'reveal' && (
            <div className="bg-white dark:bg-gray-900 rounded-md p-4 mb-4 shadow-sm ">
          <div className="mt-4 text-gray-700 dark:text-gray-200">
            <div className="text-md font-medium mb-3 text-gray-700 dark:text-gray-300">{state.current ? state.current.question : 'Waiting...'}</div>
            <div className="text-md font-bold mb-2">The correct answer was: <span className="text-green-700 dark:text-green-400">{state.correctAnswer || (state.current && state.current.correct)}</span></div>
            <div className="mb-2 font-semibold">Round Results:</div>
            <ul className="mb-2">
              {(gamePlayers || []).map(p => {
                const id = p.socketId || p.userId || p.id;
                let ans = null;
                if (Array.isArray(state.answers)) {
                  const ansEntry = state.answers.find(a => a.playerId === id);
                  ans = ansEntry ? ansEntry.answer : null;
                } else if (state.answers && typeof state.answers === 'object') {
                  ans = state.answers[id] || null;
                }
                const correct = state.correctAnswer || (state.current && state.current.correct) || null;
                const got = ans && correct ? (String(ans).trim() === String(correct).trim()) : false;
                const timeMs = (state.answerTimes && state.answerTimes[state.roundIndex] && state.answerTimes[state.roundIndex][id]) || null;
                const timeStr = formatAnswerTime(timeMs);
                const awarded = state.roundAwards && state.roundAwards[id] != null ? state.roundAwards[id] : null;
                return (
                  <li key={id} className="mb-1">
                    <span className="font-semibold">{p.username || id}</span>
                    {': '}
                    <span className={got ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>{got ? 'Correct' : (ans ? 'Wrong' : 'No Answer')}</span>
                    {ans ? (<span className="ml-2 text-xs text-gray-600 dark:text-gray-300">[{ans}]</span>) : null}
                    {timeStr ? (<span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({timeStr})</span>) : null}
                    {awarded != null && awarded > 0 ? (
                      <span className="ml-2 text-xs font-bold text-yellow-600 dark:text-yellow-400">+{awarded} pts</span>
                    ) : awarded === 0 || (ans && !got) ? (
                      <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">+0 pts</span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
          </div>
        )}
      

      {/* If countdownToStart is present, show Start button for host (first player) */}
      {state.countdownToStart && !state.started && (() => {
        const host = (state.players && state.players[0]) || null;
        const isHost = host && user && ((host.socketId && host.socketId === user.socketId) || (host.userId && host.userId === user.userId));
        if (!isHost) return null;
        return (
          <div className="mb-4">
            <button className="bg-green-600 hover:bg-green-700 text-white font-bold px-4 py-2 rounded" onClick={() => submitAction({ type: 'start' })}>Start Quiz</button>
          </div>
        );
      })()}

      {/* <div className="mb-4 text-gray-700 dark:text-gray-300">
        <div className="font-semibold mb-2 ">Scores</div>
        <ul>
          {(state.players || []).map(p => {
            const id = p.socketId || p.userId || p.id;
            const score = (state.scores && state.scores[id]) || 0;
            return (<li key={id} className={`flex justify-between py-1 ${id===meId ? 'font-semibold' : ''}`}><span>{p.username || id}</span><span>{score}</span></li>);
          })}
        </ul>
      </div> */}
    </div>
  );
}

QuizGame.Guide = function QuizGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Quiz</h3>
      <p>Answer multiple-choice questions as fast as you can. Fastest correct answers score more points.</p>
    </div>
  );
}

export default QuizGame;
